import * as decoding from "lib0/decoding";
import * as encoding from "lib0/encoding";
import {
  type Awareness,
  applyAwarenessUpdate,
  encodeAwarenessUpdate,
  removeAwarenessStates,
} from "y-protocols/awareness";
import * as sync from "y-protocols/sync";
import type * as Y from "yjs";
import type { ChannelLike, Message, RoomLike } from "./channels";

/** cs.doc/1 message type (y-protocols framing): a sync message follows. */
const MESSAGE_SYNC = 0;

export interface ProviderOptions {
  doc: Y.Doc;
  awareness: Awareness;
  room: RoomLike;
  /** Re-send our own awareness state this often, so a lost update on the lossy channel heals. */
  awarenessRefreshMs?: number;
}

interface PeerLink {
  doc?: ChannelLike;
  aware?: ChannelLike;
  /** Awareness client ids this peer has told us about (removed when it leaves). */
  awarenessClients: Set<number>;
}

const toBytes = (message: Message): Uint8Array | undefined =>
  typeof message === "string" ? undefined : message;

/**
 * Syncs a Y.Doc and its Awareness with every peer in a room (docs/PRD.md §6):
 * - `doc` (cs.doc/1, reliable): y-protocols sync (step 1/2, then incremental updates).
 * - `aware` (cs.aware/1, unreliable): y-protocols awareness, periodically refreshed.
 */
export class YjsChannelProvider {
  readonly #doc: Y.Doc;
  readonly #awareness: Awareness;
  readonly #peers = new Map<string, PeerLink>();
  readonly #cleanup: (() => void)[] = [];
  #destroyed = false;

  constructor({ doc, awareness, room, awarenessRefreshMs = 10_000 }: ProviderOptions) {
    this.#doc = doc;
    this.#awareness = awareness;

    const onUpdate = (update: Uint8Array, origin: unknown) => {
      // Relay to everyone except the peer it came from (helps while the mesh is still forming).
      const message = this.#updateMessage(update);
      for (const [peerId, link] of this.#peers) {
        if (peerId !== origin) void link.doc?.send(message).catch(() => {});
      }
    };
    doc.on("update", onUpdate);
    this.#cleanup.push(() => doc.off("update", onUpdate));

    const onAwarenessUpdate = (
      { added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
      origin: unknown,
    ) => {
      const changed = [...added, ...updated, ...removed];
      if (changed.length === 0) return;
      const message = encodeAwarenessUpdate(awareness, changed);
      for (const [peerId, link] of this.#peers) {
        if (peerId !== origin) void link.aware?.send(message).catch(() => {});
      }
    };
    awareness.on("update", onAwarenessUpdate);
    this.#cleanup.push(() => awareness.off("update", onAwarenessUpdate));

    const refresh = setInterval(() => {
      if (awareness.getLocalState() === null) return;
      const message = encodeAwarenessUpdate(awareness, [doc.clientID]);
      for (const link of this.#peers.values()) void link.aware?.send(message).catch(() => {});
    }, awarenessRefreshMs);
    this.#cleanup.push(() => clearInterval(refresh));

    this.#cleanup.push(
      room.onChannel("doc", (peerId, channel) => this.#attachDoc(peerId, channel)),
      room.onChannel("aware", (peerId, channel) => this.#attachAware(peerId, channel)),
    );
  }

  /** Peers currently linked over the doc channel. */
  get peerIds(): string[] {
    return [...this.#peers].filter(([, link]) => link.doc).map(([id]) => id);
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    for (const fn of this.#cleanup.splice(0)) fn();
    for (const peerId of [...this.#peers.keys()]) this.#forgetAwareness(peerId);
    this.#peers.clear();
  }

  #link(peerId: string): PeerLink {
    let link = this.#peers.get(peerId);
    if (!link) {
      link = { awarenessClients: new Set() };
      this.#peers.set(peerId, link);
    }
    return link;
  }

  #attachDoc(peerId: string, channel: ChannelLike): void {
    if (this.#destroyed) return;
    const link = this.#link(peerId);
    link.doc = channel;
    channel.onMessage((message) => {
      const bytes = toBytes(message);
      if (bytes) this.#onDocMessage(peerId, channel, bytes);
    });
    channel.onClose(() => {
      if (link.doc === channel) delete link.doc;
      this.#dropIfGone(peerId);
    });
    // Start sync: send our state vector; the peer answers with what we're missing (step 2).
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_SYNC);
    sync.writeSyncStep1(encoder, this.#doc);
    void channel.send(encoding.toUint8Array(encoder)).catch(() => {});
  }

  #onDocMessage(peerId: string, channel: ChannelLike, bytes: Uint8Array): void {
    try {
      const decoder = decoding.createDecoder(bytes);
      if (decoding.readVarUint(decoder) !== MESSAGE_SYNC) return; // future message types: ignore
      const reply = encoding.createEncoder();
      encoding.writeVarUint(reply, MESSAGE_SYNC);
      // Origin = peerId, so the resulting update isn't echoed back to that peer.
      sync.readSyncMessage(decoder, reply, this.#doc, peerId);
      if (encoding.length(reply) > 1)
        void channel.send(encoding.toUint8Array(reply)).catch(() => {});
    } catch {
      // Malformed message from a buggy or hostile peer: drop it.
    }
  }

  #attachAware(peerId: string, channel: ChannelLike): void {
    if (this.#destroyed) return;
    const link = this.#link(peerId);
    link.aware = channel;
    channel.onMessage((message) => {
      const bytes = toBytes(message);
      if (!bytes) return;
      try {
        const before = new Set(this.#awareness.getStates().keys());
        applyAwarenessUpdate(this.#awareness, bytes, peerId);
        // Remember which clients this peer speaks for, to clean up when it leaves.
        for (const clientId of this.#awareness.getStates().keys()) {
          if (!before.has(clientId)) link.awarenessClients.add(clientId);
        }
        for (const clientId of readAwarenessClients(bytes)) link.awarenessClients.add(clientId);
      } catch {
        // malformed: drop
      }
    });
    channel.onClose(() => {
      if (link.aware === channel) delete link.aware;
      this.#dropIfGone(peerId);
    });
    // Tell the newcomer about everyone we know (including ourselves).
    const clients = [...this.#awareness.getStates().keys()];
    if (clients.length > 0) {
      void channel.send(encodeAwarenessUpdate(this.#awareness, clients)).catch(() => {});
    }
  }

  #dropIfGone(peerId: string): void {
    const link = this.#peers.get(peerId);
    if (!link || link.doc || link.aware) return;
    this.#forgetAwareness(peerId);
    this.#peers.delete(peerId);
  }

  #forgetAwareness(peerId: string): void {
    const link = this.#peers.get(peerId);
    if (!link) return;
    const clients = [...link.awarenessClients].filter((id) => id !== this.#doc.clientID);
    if (clients.length > 0) removeAwarenessStates(this.#awareness, clients, "peer left");
  }

  #updateMessage(update: Uint8Array): Uint8Array {
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_SYNC);
    sync.writeUpdate(encoder, update);
    return encoding.toUint8Array(encoder);
  }
}

/** Client ids mentioned in an awareness update (y-protocols wire format). */
function readAwarenessClients(update: Uint8Array): number[] {
  const decoder = decoding.createDecoder(update);
  const count = decoding.readVarUint(decoder);
  const ids: number[] = [];
  for (let i = 0; i < count; i++) {
    ids.push(decoding.readVarUint(decoder));
    decoding.readVarUint(decoder); // clock
    decoding.readVarString(decoder); // state JSON
  }
  return ids;
}
